/**
 * Resolves a Godot node path on a loaded GLB by the whole path, as Godot's `get_node` does, through
 * the nodes the glTF importer builds (`godotNodes.ts`). A node lookup finds the object a Godot node
 * is, which a property write lands on. A frame lookup finds a Skeleton3D or BoneAttachment3D, whose
 * properties `glbFrameOverrides` writes, and which a node attached below the path rides with.
 */
import { flattenGlbObjects } from './glbHierarchy.js';
import { godotFrames, godotNodePaths, frameGroupPath, } from '../../../resources/formats/glb/godotNodes.js';
import { perObject } from '../../../utils/perObject.js';
import { warnOnce } from '../../../utils/warnOnce.js';
/** Built once for each entries list, which `flattenGlbObjects` builds once for each GLB. */
const pathTables = perObject((entries) => {
    const nodes = new Map();
    const frames = new Map();
    for (const entry of entries) {
        for (const path of godotNodePaths(entry.object))
            nodes.set(path, entry);
        for (const { path, kind } of godotFrames(entry.object)) {
            const group = frameGroup(entry.object, path);
            frames.set(path, { path, kind, entry, group, host: group ?? entry.object });
        }
    }
    return { nodes, frames };
});
/** The group of the frame at `path` if `object` holds one. */
function frameGroup(object, path) {
    return object.children.find((child) => frameGroupPath(child) === path) ?? null;
}
/**
 * The GLB entry whose object is the Godot node at `godotPath`, or `null` when no node of the
 * imported scene has that path, or the node is one the object only stands in for. The scene tree
 * asks through this: the viewport already reports a path that matches nothing.
 *
 * @param entries the GLB flattened by `flattenGlbObjects`
 * @param godotPath the path relative to the GLB root, such as `Skeleton/Skeleton3D/Robot`
 */
export function findGlbNode(entries, godotPath) {
    return pathTables(entries).nodes.get(godotPath) ?? null;
}
/** The Skeleton3D or BoneAttachment3D at `godotPath`, or `null` when the path names another node or none. */
export function findGlbFrame(entries, godotPath) {
    return pathTables(entries).frames.get(godotPath) ?? null;
}
/** Every Skeleton3D and BoneAttachment3D of the GLB. */
export function glbFrames(entries) {
    return [...pathTables(entries).frames.values()];
}
/**
 * {@link findGlbNode}, with a warning when the path names nothing, the first time it is asked. A
 * frame is something: `glbFrameOverrides` handles a write to it, so this returns null and stays quiet.
 */
export function matchGlbNode(entries, godotPath) {
    const entry = findGlbNode(entries, godotPath);
    if (!entry && !findGlbFrame(entries, godotPath)) {
        warnOnce(entries, godotPath, `[glbPathLookup] "${godotPath}" matches nothing in the loaded GLB: the override or node it carries is dropped`);
    }
    return entry;
}
/** The entry of the node or frame at `godotPath`. A node attached below the path rides with it. */
export function findGlbTarget(entries, godotPath) {
    const entry = findGlbNode(entries, godotPath);
    if (entry)
        return { entry, host: entry.object };
    return findGlbFrame(entries, godotPath);
}
/**
 * The object that holds the properties of the Godot node at `godotPath` inside the loaded GLB
 * `root`, or `null`. For a Skeleton3D that is its group. A BoneAttachment3D has none: it takes the
 * pose of its bone (`bone_attachment_3d.cpp:311`), so no write to its transform lasts.
 */
export function findGlbObject(root, godotPath) {
    const entries = flattenGlbObjects(root);
    const frame = findGlbFrame(entries, godotPath);
    if (frame)
        return frame.kind === 'skeleton' ? frame.group : null;
    return findGlbNode(entries, godotPath)?.object ?? null;
}
//# sourceMappingURL=glbPathLookup.js.map